/**
 * Item versions on the item page (migrations 912, 928).
 *
 * People rank a version of an item. This shows the item's current version,
 * every ranking with the version it used and whether it counts, and two flags:
 * a ranking whose item has changed since, and a cycle where groups ranked
 * different versions. The cycle owner, or anyone with decision authority on the
 * cycle, settles that flag here with one of three choices and a reason
 * (docs/9-exploration/PRD-item-versions.md §5, owner rulings R1–R5). A flag
 * never blocks anything (R3).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Callout, DataTable, Disclosure, EmptyState, TextArea, type Column } from '@/components/ds';
import { btnPrimary, btnSecondary, LABEL_MONO, status } from '@/components/mono/primitives';
import { ChoiceList } from '@/components/shared/ChoiceList';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import {
  canSettleItemVersionFlag,
  fetchItemLockState,
  fetchItemRankedVersions,
  fetchItemVersions,
  settleItemVersionFlag,
  VERSION_SETTLEMENT_CHOICES,
  type ItemLockState,
  type ItemRankedVersion,
  type ItemVersion,
  type VersionSettlement,
  type VersionSettlementChoice,
} from '@/lib/itemVersionsService';
import { formatDate } from '@/i18n/formatters';

const SHORT_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

export function ItemVersionsPanel({ itemId }: { itemId: string }) {
  const [versions, setVersions] = useState<ItemVersion[] | null>(null);
  const [rankings, setRankings] = useState<ItemRankedVersion[] | null>(null);
  const [lock, setLock] = useState<ItemLockState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [mayTick, setMayTick] = useState(0);
  const load = useCallback(() => {
    let live = true;
    setError(null);
    Promise.all([fetchItemVersions(itemId), fetchItemRankedVersions(itemId), fetchItemLockState(itemId)])
      .then(([v, r, l]) => {
        if (!live) return;
        setVersions(v);
        setRankings(r);
        setLock(l);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not load versions.');
      });
    return () => {
      live = false;
    };
  }, [itemId]);

  useEffect(() => {
    setVersions(null);
    setRankings(null);
    return load();
  }, [load, mayTick]);

  // Who may settle, per flagged cycle (R2).
  const flaggedCycles = Array.from(new Set((rankings ?? []).filter((r) => r.cycleHasMultipleVersions && r.cycleId).map((r) => r.cycleId as string)));
  const [maySettle, setMaySettle] = useState<Record<string, boolean>>({});
  const flaggedKey = flaggedCycles.join(',');
  useEffect(() => {
    let live = true;
    const ids = flaggedKey ? flaggedKey.split(',') : [];
    void Promise.all(ids.map(async (id) => [id, await canSettleItemVersionFlag(id)] as const)).then((pairs) => {
      if (live) setMaySettle(Object.fromEntries(pairs));
    });
    return () => {
      live = false;
    };
  }, [flaggedKey]);

  const settlers = Array.from(new Set((rankings ?? []).map((r) => r.settlement?.settledBy).filter((x): x is string => !!x)));
  const names = useActorDisplayMap(settlers);

  if (error) {
    return (
      <Callout tone="coral" label="Versions" title="Could not load this item's versions">
        {error}
      </Callout>
    );
  }
  if (!versions || !rankings) {
    return <p className="text-sm text-[var(--text-dim)]">Loading versions…</p>;
  }

  const current = versions[0];
  const cyclesWithMixedVersions = Array.from(
    new Set(rankings.filter((r) => r.cycleHasMultipleVersions).map((r) => r.cycleName ?? 'a cycle'))
  );
  const changedSince = rankings.filter((r) => r.changedSinceRanked);
  // One entry per flagged cycle, and one per settled cycle.
  const byCycle = (pick: (r: ItemRankedVersion) => boolean) => {
    const out = new Map<string, { cycleId: string; cycleName: string; versions: number[]; settlement: VersionSettlement | null }>();
    for (const r of rankings) {
      if (!r.cycleId || !pick(r)) continue;
      const e = out.get(r.cycleId) ?? { cycleId: r.cycleId, cycleName: r.cycleName ?? 'a cycle', versions: [], settlement: r.settlement };
      if (r.rankedVersion != null && !e.versions.includes(r.rankedVersion)) e.versions.push(r.rankedVersion);
      out.set(r.cycleId, e);
    }
    return [...out.values()].map((e) => ({ ...e, versions: e.versions.sort((a, b) => a - b) }));
  };
  const flagged = byCycle((r) => r.cycleHasMultipleVersions);
  const settled = byCycle((r) => r.settlement != null);
  const choiceLabel = (c: VersionSettlementChoice) => VERSION_SETTLEMENT_CHOICES.find((x) => x.value === c)?.label ?? c;

  const columns: Column<ItemRankedVersion>[] = [
    {
      key: 'session',
      header: 'Session',
      render: (r) => (
        <div className="min-w-0">
          <Link to={`/prioritization/sessions/${r.sessionId}?tab=results`} className="text-[var(--text-pri)] hover:underline">
            {r.sessionName}
          </Link>
          <div className="text-[13px] text-[var(--text-dim)]">
            {r.cycleName ?? 'No cycle'} · {formatDate(r.rankedAt, SHORT_DATE)}
          </div>
        </div>
      ),
    },
    { key: 'rank', header: 'Result', numeric: true, width: '5.5rem', render: (r) => `#${r.rank}` },
    {
      key: 'version',
      header: 'Ranked version',
      width: '9rem',
      render: (r) =>
        r.rankedVersion == null ? (
          <span className="text-[13px] text-[var(--text-dim)]" title="Ranked before versions were recorded">
            Not recorded
          </span>
        ) : (
          <span className="font-mono text-[13px]">v{r.rankedVersion}</span>
        ),
    },
    {
      key: 'counts',
      header: 'Counts',
      width: '10rem',
      render: (r) =>
        r.counts ? (
          <span {...status('green')}>Counts</span>
        ) : r.settlement?.choice === 'rerank_newest' ? (
          <span {...status('mustard')}>Re-rank on v{r.settlement.countedVersion}</span>
        ) : (
          <span {...status('neutral')}>Informational</span>
        ),
    },
    {
      key: 'flag',
      header: 'Since then',
      width: '13rem',
      render: (r) =>
        r.rankedVersion == null ? (
          <span className="text-[13px] text-[var(--text-dim)]">—</span>
        ) : r.changedSinceRanked ? (
          <span {...status('mustard')}>Changed · now v{r.currentVersion}</span>
        ) : (
          <span {...status('green')}>Unchanged</span>
        ),
    },
  ];

  return (
    <div className="space-y-4" data-testid="item-versions-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className={LABEL_MONO}>Versions</p>
        {current && (
          <span className="text-[13px] text-[var(--text-dim)]">
            Now version {current.version}
            {current.changed.length > 0 ? ` · last change: ${current.changed.join(', ')} on ${formatDate(current.createdAt, SHORT_DATE)}` : ''}
          </span>
        )}
      </div>

      {lock?.locked && (
        <Callout tone="slate" label="Locked after ranking" title="What this item says is locked">
          A version of it has been ranked, so its title, description, categories and altitude can only be changed by a workspace admin or
          someone with decision authority. {lock.canChange ? 'You can change it; your change becomes a new version.' : 'You can still change its other fields.'}
        </Callout>
      )}

      {flagged.map((f) => (
        <SettleFlag
          key={f.cycleId}
          itemId={itemId}
          cycleId={f.cycleId}
          cycleName={f.cycleName}
          versions={f.versions}
          maySettle={!!maySettle[f.cycleId]}
          onSettled={() => setMayTick((t) => t + 1)}
        />
      ))}

      {settled.map((st) =>
        st.settlement ? (
          <Callout key={st.cycleId} tone="slate" label="Settled" title={`${st.cycleName}: ${choiceLabel(st.settlement.choice)}${st.settlement.countedVersion != null ? ` (version ${st.settlement.countedVersion})` : ''}`}>
            “{st.settlement.reason}”
            {st.settlement.settledBy ? ` — ${names[st.settlement.settledBy] ?? 'someone'}` : ''}
            {st.settlement.settledAt ? `, ${formatDate(st.settlement.settledAt, SHORT_DATE)}` : ''}. Versions {st.versions.join(', ')} were ranked in this cycle.
          </Callout>
        ) : null,
      )}
      {cyclesWithMixedVersions.length === 0 && changedSince.length > 0 && (
        <Callout tone="slate" label="Changed since ranked">
          The item has changed since {changedSince.length === 1 ? 'one ranking' : `${changedSince.length} rankings`}. Those groups ranked an earlier
          version, shown below.
        </Callout>
      )}

      {rankings.length > 0 ? (
        <DataTable columns={columns} rows={rankings} getRowKey={(r) => r.resultId} density="compact" caption="Rankings of this item and the version each used" captionHidden />
      ) : (
        <EmptyState title="Not ranked yet" description="When a session ranks this item, the version it ranked is recorded here." />
      )}

      {current && rankings.some((r) => r.rankedVersion != null && r.rankedVersion < current.version) && (
        <Disclosure title="Compare a ranked version with today">
          <div className="space-y-4">
            {Array.from(new Set(rankings.map((r) => r.rankedVersion).filter((v): v is number => v != null && v < current.version)))
              .sort((a, b) => b - a)
              .map((v) => {
                const then = versions.find((x) => x.version === v);
                if (!then) return null;
                return (
                  <div key={v} className="grid grid-cols-1 gap-3 border-b pb-3 last:border-b-0 sm:grid-cols-2" style={{ borderColor: 'var(--rule)' }}>
                    <VersionText label={`Ranked: version ${v}`} v={then} />
                    <VersionText label={`Today: version ${current.version}`} v={current} />
                  </div>
                );
              })}
          </div>
        </Disclosure>
      )}

      {versions.length > 0 && (
        <Disclosure title={`All versions (${versions.length})`}>
          <ol className="space-y-3">
            {versions.map((v) => (
              <li key={v.id} className="border-b pb-3 last:border-b-0" style={{ borderColor: 'var(--rule)' }}>
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-mono text-[13px] text-[var(--text-pri)]">v{v.version}</span>
                  <span className="text-[13px] text-[var(--text-dim)]">
                    {formatDate(v.createdAt, SHORT_DATE)}
                    {v.changed.length > 0 ? ` · changed: ${v.changed.join(', ')}` : ' · first recorded'}
                  </span>
                </div>
                <div className="mt-1 text-[var(--text-pri)]">{v.title}</div>
                {v.description && <div className="mt-0.5 text-sm text-[var(--text-sec)]">{v.description}</div>}
                <div className="mt-0.5 text-[13px] text-[var(--text-dim)]">
                  {v.itemCategory ?? 'No category'} × {v.altitudeLevel != null ? `altitude ${v.altitudeLevel}` : 'no altitude'}
                </div>
              </li>
            ))}
          </ol>
        </Disclosure>
      )}
    </div>
  );
}

function VersionText({ label, v }: { label: string; v: ItemVersion }) {
  return (
    <div className="min-w-0">
      <div className={LABEL_MONO}>{label}</div>
      <div className="mt-1 text-[var(--text-pri)]">{v.title}</div>
      {v.description && <div className="mt-0.5 text-sm text-[var(--text-sec)]">{v.description}</div>}
      <div className="mt-0.5 text-[13px] text-[var(--text-dim)]">
        {v.itemCategory ?? 'No category'} × {v.altitudeLevel != null ? `altitude ${v.altitudeLevel}` : 'no altitude'}
      </div>
    </div>
  );
}

/** One flagged cycle: what happened, and — for the cycle owner or decision authority — the three choices. */
function SettleFlag({
  itemId,
  cycleId,
  cycleName,
  versions,
  maySettle,
  onSettled,
}: {
  itemId: string;
  cycleId: string;
  cycleName: string;
  versions: number[];
  maySettle: boolean;
  onSettled: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<VersionSettlementChoice | ''>('');
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = choice !== '' && reason.trim() !== '' && (choice !== 'version_counts' || counted !== '');

  const save = async () => {
    if (!choice) return;
    setSaving(true);
    setError(null);
    const err = await settleItemVersionFlag({ cycleId, itemId, choice, countedVersion: choice === 'version_counts' ? Number(counted) : null, reason: reason.trim() });
    setSaving(false);
    if (err) setError(err);
    else onSettled();
  };

  return (
    <Callout tone="mustard" label="Different versions ranked" title={`Groups ranked versions ${versions.join(', ')} of this item in ${cycleName}`}>
      <p>
        Each group judged what the item said at the time. Decide whether the differences matter. This never blocks ratifying the list; it shows
        until someone settles it.
      </p>
      {maySettle && !open && (
        <button {...btnSecondary('mt-2')} onClick={() => setOpen(true)}>
          Settle
        </button>
      )}
      {!maySettle && <p className="mt-1 text-[13px]">The cycle owner, or someone with decision authority on the cycle, settles it.</p>}
      {open && (
        <div className="mt-3 space-y-3">
          <ChoiceList label="How to settle" stacked options={VERSION_SETTLEMENT_CHOICES} value={choice} onPick={(v) => setChoice(v as VersionSettlementChoice)} />
          {choice === 'version_counts' && (
            <div>
              <div className={LABEL_MONO}>The version that counts</div>
              <div className="mt-1.5">
                <ChoiceList label="The version that counts" compact options={versions.map((v) => ({ value: String(v), label: `Version ${v}` }))} value={counted} onPick={setCounted} />
              </div>
            </div>
          )}
          <label className="block">
            <span className={LABEL_MONO}>Why</span>
            <TextArea aria-label="Why" rows={2} value={reason} placeholder="Kept with the decision" onChange={(e) => setReason(e.target.value)} />
          </label>
          {error && <p className="text-sm text-[var(--coral-text)]">{error}</p>}
          <div className="flex gap-2">
            <button {...btnPrimary()} disabled={!ready || saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Settle'}
            </button>
            <button {...btnSecondary()} disabled={saving} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Callout>
  );
}

export default ItemVersionsPanel;
